import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { endpoints, type ConfigKnob } from "@/lib/api";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { SectionTabs, SYSTEM_TABS } from "@/components/SectionTabs";

// Settings — v2.2.5 read-only reference of operator-tunable config
// values. Closes pain point #1-#3 from the v2.2.x platform inventory:
// operators no longer have to SSH + cat config.yaml to know what's
// adjustable. Editing is still manual (sops + restart) — by design,
// see commit v2.2.5 message for the safety rationale.
export default function Settings() {
  const q = useQuery({
    queryKey: ["system-config-knobs"],
    queryFn: () => endpoints.systemConfigKnobs(),
  });

  const grouped = useMemo(() => {
    const out = new Map<string, ConfigKnob[]>();
    for (const k of q.data?.knobs ?? []) {
      const arr = out.get(k.category) ?? [];
      arr.push(k);
      out.set(k.category, arr);
    }
    return Array.from(out.entries());
  }, [q.data]);

  return (
    <div className="h-full overflow-auto">
      <SectionTabs tabs={SYSTEM_TABS} />
      <div className="p-6">
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-xl font-semibold">配置参考</h1>
        <span className="text-xs text-muted-foreground">
          {q.data?.knobs.length ?? 0} 个可调参数 (只读)
        </span>
      </div>

      {q.data?.hot_reload_hint && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          ⚠ {q.data.hot_reload_hint}
        </div>
      )}

      {q.isLoading && <p className="text-sm text-muted-foreground">加载中…</p>}
      {q.isError && (
        <p className="text-sm text-destructive">
          加载失败:{(q.error as Error).message}
        </p>
      )}

      <div className="space-y-4">
        {grouped.map(([category, items]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="text-sm">{category}</CardTitle>
              <CardDescription>
                {items.length} 项 · 修改后 systemctl restart bitbase-kol
              </CardDescription>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">路径</th>
                    <th className="py-2 pr-3">当前值</th>
                    <th className="py-2 pr-3">说明</th>
                    <th className="py-2 pr-3">修改位置</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((k) => (
                    <KnobRow key={k.path} knob={k} />
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))}
      </div>
      </div>
    </div>
  );
}

function KnobRow({ knob }: { knob: ConfigKnob }) {
  return (
    <tr className="border-b border-border last:border-0 align-top">
      <td className="py-2 pr-3 align-top">
        <code className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
          {knob.path}
        </code>
      </td>
      <td className="py-2 pr-3 align-top">
        <KnobValue knob={knob} />
      </td>
      <td className="py-2 pr-3 align-top text-xs text-muted-foreground">
        {knob.description}
      </td>
      <td className="py-2 pr-3 align-top">
        <code className="text-[10px] text-muted-foreground">{knob.edit_hint}</code>
      </td>
    </tr>
  );
}

function KnobValue({ knob }: { knob: ConfigKnob }) {
  const value = knob.value;
  // Empty string / null → muted "(空)" so we don't show a phantom blank.
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground italic">(空)</span>;
  }
  if (knob.type === "string") {
    const str = String(value);
    // Long URLs / tokens — truncate with a tooltip-via-title.
    if (str.length > 60) {
      return (
        <code className="text-[11px]" title={str}>
          {str.slice(0, 60)}…
        </code>
      );
    }
    return <code className="text-[11px]">{str}</code>;
  }
  return (
    <span className="font-mono tabular-nums">
      <Badge variant="secondary" className="text-[10px]">
        {String(value)}
      </Badge>
    </span>
  );
}
